import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, Recommend } from '@mui/icons-material';
import { Services, ServiceCategory, ServiceCategoryLabels, ServicesLabels } from '../../utils/enums';
import { recommendationsAPI, citiesAPI } from '../../services/api';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

function normalizeRecommendationsPayload(rawPayload) {
  if (Array.isArray(rawPayload)) {
    return rawPayload;
  }
  if (rawPayload) {
    return [rawPayload];
  }
  return [];
}

function getExistingRecommendationsList(payload, cityId, serviceId, serviceCategoryTypeId) {
  if (Array.isArray(payload)) {
    const match = payload.find(
      (p) =>
        p.cityId === cityId &&
        p.serviceId === serviceId &&
        p.serviceCategoryTypeId === serviceCategoryTypeId
    );
    return match?.listOfRecommendations || [];
  }
  return payload?.listOfRecommendations || [];
}

// Helper to merge new recommendations with existing ones for a given service category
const mergeNewWithExistingRecommendations = async (cityId, serviceId, serviceCategoryTypeId, newItems) => {
  try {
    const getResponse = await recommendationsAPI.getRecommendations(
      cityId,
      serviceId,
      serviceCategoryTypeId
    );
    const payload = getResponse?.data?.payload;
    const existingList = getExistingRecommendationsList(payload, cityId, serviceId, serviceCategoryTypeId);

    const existingForApi = (existingList || []).map(rec => ({
      Index: Number.parseInt(rec.index) || 0,
      Description: (rec.description || '').trim()
    }));
    const nextIndex = existingForApi.length > 0
      ? Math.max(...existingForApi.map(r => r.Index), 0) + 1
      : 1;
    const newItemsWithIndices = newItems.map((item, i) => ({
      ...item,
      Index: nextIndex + i
    }));
    return [...existingForApi, ...newItemsWithIndices];
  } catch (getErr) {
    console.warn('Could not fetch existing recommendations, submitting only new items:', getErr);
    return newItems;
  }
};

const Recommendations = () => {
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [recommendations, setRecommendations] = useState([]);
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState({
    cityId: '',
    serviceId: '',
    serviceCategoryTypeId: '',
    serviceCategoryTypeName: '',
    listOfRecommendations: []
  });
  const [recommendationItemDialogOpen, setRecommendationItemDialogOpen] = useState(false);
  const [currentRecommendationItem, setCurrentRecommendationItem] = useState({
    index: 1,
    description: ''
  });

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true) {
        setCities(response.data.payload || []);
        // Set first city as default
        if (response.data.payload?.length > 0) {
          setSelectedCity(response.data.payload[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchRecommendations = useCallback(async (cityId = selectedCity, serviceId = selectedService, serviceCategoryTypeId = selectedCategory) => {
    setIsLoading(true);
    try {
      const response = await recommendationsAPI.getRecommendations(cityId || null, serviceId || null, serviceCategoryTypeId || null);
      if (response?.data?.success === true) {
        const payloadArray = normalizeRecommendationsPayload(response.data.payload);

        setRecommendations({
          payload: payloadArray,
          totalRecord: payloadArray.length
        });
      } else {
        console.warn('Invalid response format:', response);
        setRecommendations({ payload: [], totalRecord: 0 });
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations({ payload: [], totalRecord: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [selectedCity, selectedService, selectedCategory]);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    // Set first service as default on component mount
    const firstService = Object.values(Services)[0];
    if (firstService) {
      setSelectedService(firstService.toString());
      
      // Find matching categories for the selected service
      const matchingCategories = Object.entries(ServiceCategory)
        .filter(([key, value]) => {
          if (firstService === Services.Student) {
            return value >= 1 && value <= 5;
          } else if (firstService === Services.Resident) {
            return value === 7 || value === 10;
          }
          return false;
        });

      // Set first matching category as default
      if (matchingCategories.length > 0) {
        const [, firstCategoryId] = matchingCategories[0];
        setSelectedCategory(firstCategoryId.toString());
      }
    }
  }, []); // Run only once on mount

  useEffect(() => {
    if (selectedCity && selectedService && selectedCategory) {
      fetchRecommendations();
    }
  }, [selectedCity, selectedService, selectedCategory, fetchRecommendations]);

  useEffect(() => {
    const docsArray = Array.isArray(recommendations?.payload) ? recommendations.payload : [];
    let filtered = docsArray;
    
    if (selectedService) {
      filtered = filtered.filter(doc => doc.serviceId === Number.parseInt(selectedService));
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(doc => doc.serviceCategoryTypeId === Number.parseInt(selectedCategory));
    }

    if (selectedCity) {
      filtered = filtered.filter(doc => doc.cityId === Number.parseInt(selectedCity));
    }
    
    setFilteredRecommendations(filtered);
  }, [recommendations?.payload, selectedService, selectedCategory, selectedCity]);

  const handleOpen = (recommendation = null) => {
    if (recommendation) {
      // Ensure serviceCategoryTypeId is a string and serviceCategoryTypeName is set
      const categoryId = recommendation.serviceCategoryTypeId?.toString() || '';
      const categoryName = recommendation.serviceCategoryTypeName || 
        (categoryId ? ServiceCategoryLabels[Number.parseInt(categoryId)] : '');
      
      setCurrentRecommendation({
        ...recommendation,
        serviceCategoryTypeId: categoryId,
        serviceCategoryTypeName: categoryName,
        serviceId: recommendation.serviceId?.toString() || '',
        cityId: recommendation.cityId?.toString() || ''
      });
      setEditMode(true);
    } else {
      // Pre-fill Add form with current page filters so cityId, serviceId, serviceCategoryTypeId come from form (user selection)
      const categoryName = selectedCategory ? (ServiceCategoryLabels[Number.parseInt(selectedCategory)] || '') : '';
      setCurrentRecommendation({
        cityId: selectedCity || '',
        serviceId: selectedService || '',
        serviceCategoryTypeId: selectedCategory || '',
        serviceCategoryTypeName: categoryName,
        listOfRecommendations: []
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'serviceId') {
      // Reset category when service changes
      const matchingCategories = Object.entries(ServiceCategory)
        .filter(([key, val]) => {
          if (Number.parseInt(value) === Services.Student) {
            return val >= 1 && val <= 5;
          } else if (Number.parseInt(value) === Services.Resident) {
            return val === 7 || val === 10;
          }
          return false;
        });

      const newCategoryId = matchingCategories.length > 0 ? matchingCategories[0][1].toString() : '';
      const newCategoryName = newCategoryId ? ServiceCategoryLabels[Number.parseInt(newCategoryId)] : '';

      setCurrentRecommendation({
        ...currentRecommendation,
        serviceId: value,
        serviceCategoryTypeId: newCategoryId,
        serviceCategoryTypeName: newCategoryName
      });
    } else if (name === 'serviceCategoryTypeId') {
      const categoryName = ServiceCategoryLabels[Number.parseInt(value)] || '';
      setCurrentRecommendation({
        ...currentRecommendation,
        serviceCategoryTypeId: value,
        serviceCategoryTypeName: categoryName
      });
    } else {
      setCurrentRecommendation({
        ...currentRecommendation,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission if called from form
    if (e) {
      e.preventDefault();
    }

    console.log('handleSubmit called', { currentRecommendation, editMode });

    // Validate required fields
    if (!currentRecommendation.cityId || !currentRecommendation.serviceId || 
        !currentRecommendation.serviceCategoryTypeId || !currentRecommendation.serviceCategoryTypeName) {
      alert('Please fill in all required fields (City, Service, and Service Category)');
      console.log('Validation failed: Missing required fields', {
        cityId: currentRecommendation.cityId,
        serviceId: currentRecommendation.serviceId,
        serviceCategoryTypeId: currentRecommendation.serviceCategoryTypeId,
        serviceCategoryTypeName: currentRecommendation.serviceCategoryTypeName
      });
      return;
    }

    if (!currentRecommendation.listOfRecommendations || currentRecommendation.listOfRecommendations.length === 0) {
      alert('Please add at least one recommendation');
      console.log('Validation failed: No recommendations added');
      return;
    }

    // Validate that all recommendations have descriptions
    const hasEmptyDescriptions = currentRecommendation.listOfRecommendations.some(
      rec => !rec.description || rec.description.trim() === ''
    );
    if (hasEmptyDescriptions) {
      alert('Please ensure all recommendations have a description');
      console.log('Validation failed: Empty descriptions found');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use only add/edit form values (selected by user in the dialog)
      const cityId = Number.parseInt(currentRecommendation.cityId, 10);
      const serviceId = Number.parseInt(currentRecommendation.serviceId, 10);
      const serviceCategoryTypeId = Number.parseInt(currentRecommendation.serviceCategoryTypeId, 10);
      const newItems = currentRecommendation.listOfRecommendations.map(rec => ({
        Index: Number.parseInt(rec.index, 10) || 0,
        Description: rec.description.trim()
      }));

      let listToSubmit = newItems;

      if (!editMode) {
        listToSubmit = await mergeNewWithExistingRecommendations(cityId, serviceId, serviceCategoryTypeId, newItems);
      }

      const dataToSubmit = {
        CityId: cityId,
        ServiceId: serviceId,
        ServiceCategoryTypeId: serviceCategoryTypeId,
        ServiceCategoryTypeName: currentRecommendation.serviceCategoryTypeName,
        ListOfRecommendations: listToSubmit
      };

      console.log('Submitting data:', dataToSubmit);
      console.log('API method:', editMode ? 'updateRecommendation' : 'createRecommendation');

      const response = editMode 
        ? await recommendationsAPI.updateRecommendation(dataToSubmit)
        : await recommendationsAPI.createRecommendation(dataToSubmit);
      
      console.log('API Response:', response);

      if (response?.data?.success === true) {
        // Refresh recommendations list
        await fetchRecommendations();
        handleClose();
      } else {
        console.error('API returned success=false:', response?.data);
        alert('Error saving recommendations: ' + (response?.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving recommendations:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert('Error saving recommendations: ' + (error.response?.data?.message || error.message || 'Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (cityId, serviceId, serviceCategoryTypeId) => {
    if (browserGlobal.confirm('Are you sure you want to delete this recommendation?')) {
      setIsLoading(true);
      try {
        const response = await recommendationsAPI.deleteRecommendation(
          Number.parseInt(cityId),
          Number.parseInt(serviceId),
          Number.parseInt(serviceCategoryTypeId)
        );

        if (response?.data?.success === true) {
          await fetchRecommendations();
        } else {
          alert('Error deleting recommendation: ' + (response?.data?.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error deleting recommendation:', error);
        alert('Error deleting recommendation: ' + (error.response?.data?.message || error.message || 'Please try again.'));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddRecommendation = () => {
    setCurrentRecommendationItem({
      index: currentRecommendation.listOfRecommendations.length + 1,
      description: ''
    });
    setRecommendationItemDialogOpen(true);
  };

  const handleRecommendationItemSubmit = () => {
    if (currentRecommendationItem.description) {
      setCurrentRecommendation({
        ...currentRecommendation,
        listOfRecommendations: [
          ...currentRecommendation.listOfRecommendations,
          currentRecommendationItem
        ]
      });
      setRecommendationItemDialogOpen(false);
    }
  };

  const handleRemoveRecommendation = (index) => {
    const updatedRecommendations = [...currentRecommendation.listOfRecommendations];
    updatedRecommendations.splice(index, 1);
    // Update indices
    const reorderedRecommendations = updatedRecommendations.map((rec, idx) => ({
      ...rec,
      index: idx + 1
    }));
    setCurrentRecommendation({
      ...currentRecommendation,
      listOfRecommendations: reorderedRecommendations
    });
  };

  const renderRecommendationsTableContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredRecommendations.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No recommendations found</Typography>
        </Box>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRecommendations.flatMap(recommendation =>
            recommendation.listOfRecommendations?.map((rec, index) => (
              <TableRow key={`${recommendation.serviceId}-${recommendation.serviceCategoryTypeId}-${index}`}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Recommend fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle2">{rec.index}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{rec.description}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <IconButton
                      onClick={() => handleOpen(recommendation)}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.50' }
                      }}
                      title="Edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    {isSuperAdmin && (
                      <IconButton
                        onClick={() => handleDelete(recommendation.cityId, recommendation.serviceId, recommendation.serviceCategoryTypeId)}
                        size="small"
                        sx={{
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.50' }
                        }}
                        title="Delete"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )) || []
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" color="primary">Recommendations</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            Add Recommendations
          </Button>
        )}
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={selectedCity}
                label="City"
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={selectedService}
                label="Service Type"
                onChange={(e) => {
                  const newService = e.target.value;
                  setSelectedService(newService);
                  
                  // Reset and update category based on selected service
                  const matchingCategories = Object.entries(ServiceCategory)
                    .filter(([key, value]) => {
                      if (Number.parseInt(newService) === Services.Student) {
                        return value >= 1 && value <= 5;
                      } else if (Number.parseInt(newService) === Services.Resident) {
                        return value === 7 || value === 10;
                      }
                      return false;
                    });

                  if (matchingCategories.length > 0) {
                    const [, firstCategoryId] = matchingCategories[0];
                    setSelectedCategory(firstCategoryId.toString());
                  } else {
                    setSelectedCategory('');
                  }
                }}
              >
                {Object.entries(Services).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {ServicesLabels[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Service Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Service Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {Object.entries(ServiceCategory)
                  .filter(([key, value]) => {
                    if (Number.parseInt(selectedService) === Services.Student) {
                      return value >= 1 && value <= 5;
                    } else if (Number.parseInt(selectedService) === Services.Resident) {
                      return value === 7 || value === 10;
                    }
                    return false;
                  })
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {ServiceCategoryLabels[value]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box component={Paper} sx={{ overflowX: 'auto' }}>
        {renderRecommendationsTableContent()}
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Recommendations' : 'Add New Recommendations'}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select
                    name="cityId"
                    value={currentRecommendation.cityId}
                    onChange={handleChange}
                    label="City"
                  >
                    {cities.map(city => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Service</InputLabel>
                  <Select
                    name="serviceId"
                    value={currentRecommendation.serviceId}
                    onChange={handleChange}
                    label="Service"
                  >
                    {Object.entries(Services).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {ServicesLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Service Category</InputLabel>
                  <Select
                    name="serviceCategoryTypeId"
                    value={currentRecommendation.serviceCategoryTypeId}
                    onChange={handleChange}
                    label="Service Category"
                    disabled={!currentRecommendation.serviceId}
                  >
                    {Object.entries(ServiceCategory)
                      .filter(([key, value]) => {
                        if (Number.parseInt(currentRecommendation.serviceId) === Services.Student) {
                          return value >= 1 && value <= 5;
                        } else if (Number.parseInt(currentRecommendation.serviceId) === Services.Resident) {
                          return value === 7 || value === 10;
                        }
                        return false;
                      })
                      .map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {ServiceCategoryLabels[value]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Recommendations</Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddRecommendation}
                    sx={{ color: '#0F3D3E' }}
                  >
                    Add Recommendation
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {currentRecommendation.listOfRecommendations.map((rec, index) => (
                    <React.Fragment key={rec.index}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 1 }}>
                          <Recommend sx={{ color: 'primary.main', cursor: 'move' }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <TextField
                              fullWidth
                              label="Description"
                              value={rec.description}
                              onChange={(e) => {
                                const updatedRecommendations = [...currentRecommendation.listOfRecommendations];
                                updatedRecommendations[index] = {
                                  ...rec,
                                  description: e.target.value
                                };
                                setCurrentRecommendation({
                                  ...currentRecommendation,
                                  listOfRecommendations: updatedRecommendations
                                });
                              }}
                              multiline
                              rows={2}
                            />
                          </Box>
                          {index > 0 && (
                            <IconButton
                              onClick={() => handleRemoveRecommendation(index)}
                              sx={{ color: 'error.main' }}
                              size="small"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddRecommendation}
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Add Recommendation
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting} type="button">Cancel</Button>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }}
            variant="contained"
            disabled={isSubmitting}
            type="button"
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666'
              }
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} /> : null}
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recommendationItemDialogOpen} onClose={() => setRecommendationItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Recommendation</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={currentRecommendationItem.description}
                  onChange={(e) => setCurrentRecommendationItem({
                    ...currentRecommendationItem,
                    description: e.target.value
                  })}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecommendationItemDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRecommendationItemSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Recommendations;
